<template>
  <el-dialog
    title="创建万能保用户"
    :visible.sync="dialogVisible"
    width="600px"
    :before-close="handleClose"
    @close="handleClose"
  >
    <el-form
      ref="createForm"
      :model="form"
      :rules="rules"
      label-width="120px"
      v-loading="loading"
    >
      <el-form-item label="平台名称：" prop="appId">
        <el-select
          v-model="form.appId"
          filterable
          remote
          :remote-method="fetchAppList"
          :loading="appLoading"
          placeholder="请输入平台名称搜索"
          style="width: 100%"
        >
          <el-option
            v-for="item in appList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          ></el-option>
        </el-select>
      </el-form-item>
      
      <el-form-item label="渠道：" prop="channelId">
        <el-select
          v-model="form.channelId"
          filterable
          remote
          :remote-method="filterChannelList"
          :loading="channelLoading"
          placeholder="请输入渠道名称搜索"
          style="width: 100%"
          :disabled="!form.appId"
        >
          <el-option
            v-for="item in filteredChannelList"
            :key="item.id"
            :label="item.realname + (item.alias ? ' (' + item.alias + ')' : '')"
            :value="item.id"
          ></el-option>
        </el-select>
      </el-form-item>
      
      <el-form-item
        label="用户名："
        prop="username"
        :error="accountError"
        :class="{ 'account-required': !form.mobile }"
      >
        <el-input
          v-model="form.username"
          placeholder="请输入用户名"
          maxlength="32"
          @input="handleAccountInput"
        ></el-input>
      </el-form-item>
      
      <el-form-item
        ref="mobileFormItem"
        label="手机号："
        prop="mobile"
        :class="{ 'account-required': !form.username }"
      >
        <el-input
          v-model="form.mobile"
          placeholder="请输入手机号"
          maxlength="11"
          @input="handleAccountInput"
        ></el-input>
      </el-form-item>
      
      <el-form-item label="价格方案：" prop="priceId">
        <el-select
          v-model="form.priceId"
          filterable
          placeholder="请选择价格方案"
          style="width: 100%"
        >
          <el-option
            v-for="item in pricePlanOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          ></el-option>
        </el-select>
      </el-form-item>
    </el-form>
    
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取 消</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="submitting">保 存</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { createHyxUser, getAppList, getChannelList, getHyxPriceList } from '@/api/member'

export default {
  name: 'CreateHyxUser',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    // 手机号验证规则
    const validateMobile = (rule, value, callback) => {
      if (!value) {
        callback()
        return
      }
      
      const regPhone = /^(0|86|17951)?(13[0-9]|15[012356789]|17[0-9]|18[0-9]|14[57])[0-9]{8}$/
      if (!regPhone.test(value)) {
        callback(new Error('请输入正确的手机号'))
        return
      }
      
      callback()
    }
    
    return {
      dialogVisible: this.visible,
      loading: false,
      submitting: false,
      accountError: '',
      appLoading: false,
      channelLoading: false,
      form: {
        appId: '',
        channelId: '',
        username: '',
        mobile: '',
        priceId: ''
      },
      rules: {
        appId: [
          { required: true, message: '请选择平台', trigger: 'change' }
        ],
        channelId: [
          { required: true, message: '请选择渠道', trigger: 'change' }
        ],
        mobile: [
          { validator: validateMobile, trigger: 'blur' }
        ],
        priceId: [
          { required: true, message: '请选择价格方案', trigger: 'change' }
        ]
      },
      appList: [],
      channelList: [],
      filteredChannelList: [],
      pricePlanOptions: []
    }
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal
      if (newVal) {
        this.fetchAppList()
        this.fetchPriceList()
      }
    },
    'form.appId'(newVal) {
      if (newVal) {
        this.fetchChannelList(newVal)
      } else {
        this.filteredChannelList = []
        this.form.channelId = ''
      }
    }
  },
  methods: {
    // 获取平台列表（支持模糊搜索）
    async fetchAppList(query) {
      this.appLoading = true
      try {
        const params = query ? { name: query } : {}
        const res = await getAppList(params)
        if (res.err_code === 0) {
          this.appList = res.rows || []
        }
      } catch (error) {
        console.error('获取平台列表失败:', error)
      } finally {
        this.appLoading = false
      }
    },
    
    // 获取渠道列表
    async fetchChannelList(appId) {
      this.channelLoading = true
      try {
        const res = await getChannelList({ app_id: appId })
        if (res.err_code === 0) {
          this.channelList = res.rows || []
          this.filteredChannelList = this.channelList
        }
      } catch (error) {
        console.error('获取渠道列表失败:', error)
      } finally {
        this.channelLoading = false
      }
    },
    
    // 过滤渠道列表（支持模糊搜索）
    filterChannelList(query) {
      if (query) {
        this.filteredChannelList = this.channelList.filter(item => {
          const nameMatch = item.name.toLowerCase().includes(query.toLowerCase())
          const aliasMatch = item.alias && item.alias.toLowerCase().includes(query.toLowerCase())
          return nameMatch || aliasMatch
        })
      } else {
        this.filteredChannelList = this.channelList
      }
    },
    
    // 获取价格方案列表
    async fetchPriceList() {
      try {
        const res = await getHyxPriceList()
        if (res.err_code === 0) {
          this.pricePlanOptions = res.rows || []
        }
      } catch (error) {
        console.error('获取价格方案列表失败:', error)
      }
    },
    
    handleClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
      this.resetForm()
    },
    handleAccountInput() {
      this.accountError = ''
      this.$nextTick(() => {
        if (this.$refs.mobileFormItem) {
          this.$refs.mobileFormItem.clearValidate()
        }
      })
    },
    resetForm() {
      this.$refs.createForm.resetFields()
      this.form = {
        appId: '',
        channelId: '',
        username: '',
        mobile: '',
        priceId: ''
      }
      this.filteredChannelList = []
      this.accountError = ''
    },
    handleSubmit() {
      const accountValid = Boolean(this.form.username || this.form.mobile)
      this.accountError = accountValid ? '' : '用户名和手机号至少填写一个'
      this.$refs.createForm.validate(async valid => {
        if (!valid || !accountValid) return
        
        this.submitting = true
        try {
          const params = {
            app_id: Number(this.form.appId),
            channel_id: Number(this.form.channelId),
            username: this.form.username,
            mobile: this.form.mobile,
            price_id: Number(this.form.priceId)
          }
          
          // 调用创建用户接口
          const res = await createHyxUser(params)
          if (res.err_code === 0) {
            this.$message.success('用户创建成功')
            this.$emit('success')
            this.handleClose()
          } else {
            this.$message.error(res.err_msg || '创建用户失败')
          }
        } catch (error) {
          console.error('创建用户失败:', error)
          this.$message.error('创建用户失败: ' + error.message)
        } finally {
          this.submitting = false
        }
      })
    }
  }
}
</script>

<style lang="less" scoped>
.dialog-footer {
  text-align: center;
}

.account-required /deep/ .el-form-item__label::before {
  content: '*';
  color: #f56c6c;
  margin-right: 4px;
}
</style>
